import MeaPage from "../../components/MeaPage";
import { useRef, useState, useEffect } from "react";
import RegistrationsListComponent from "./components/RegistrationsListComponent";
import MeaLink from "../../components/MeaLink";
import { useAuthContext } from "../../hooks/useAuthContext";
import { BreadCrumb } from "primereact/breadcrumb";
import RegistrationsCardsComponent from "./components/RegistrationsCardsComponent";
import { Button } from "primereact/button";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import trainingsAPI from "../../services/trainings";
import accountsAPI from "../../services/accounts";

const RegistrationsListPage = () => {
  const navigate = useNavigate();
  const { currentMembership, auth, updateCurrentMembershipPreferences } = useAuthContext();
  const schoolId = currentMembership?.school?.id;
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [modalVisible, setModalVisible] = useState(true);
  const listRef = useRef();
  const [searchParams] = useSearchParams();
  const target = searchParams.get("target");
  const targetSection = target === "payments"
    ? "payments"
    : target === "campaigns"
      ? "campaigns"
      : target === "settings"
        ? "settings"
        : "candidates";
  const targetLabel = targetSection === "payments"
    ? "Paiements"
    : targetSection === "campaigns"
      ? "Campagnes d'e-mails"
      : targetSection === "settings"
        ? "Gestion de l'espace famille"
        : "Inscriptions";

  const lastVisitedTrainingId = currentMembership?.preferences?.last_visited_training;

  useEffect(() => {
    if (lastVisitedTrainingId) {
      navigate(`/school/${schoolId}/admin/registrations/${lastVisitedTrainingId}/${targetSection}`, { replace: true });
    }
  }, [targetSection, lastVisitedTrainingId, schoolId, navigate]);

  const trainingsQuery = trainingsAPI.useGetList(
    schoolId,
    auth?.access_token,
    1,
    1000,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  const handleSelectTraining = async (trainingId) => {
    setModalVisible(false);
    try {
      const updatedPrefs = await accountsAPI.UpdateMemberPreferences(auth?.access_token, currentMembership?.id, trainingId);
      if (updatedPrefs) {
        updateCurrentMembershipPreferences(updatedPrefs);
      } else {
        const fallbackPrefs = currentMembership?.preferences 
          ? { ...currentMembership.preferences, last_visited_training: trainingId }
          : { last_visited_training: trainingId };
        updateCurrentMembershipPreferences(fallbackPrefs);
      }
    } catch (err) {
      console.error("Failed to save training preference", err);
    }
    navigate(`/school/${schoolId}/admin/registrations/${trainingId}/payments`);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "Ouvert";
      case "DRAFT":
        return "Brouillon";
      case "SUSPENDED":
        return "Suspendu";
      case "ARCHIVED":
        return "Archivé";
      default:
        return status;
    }
  };
  
  const breadcrum = [
    {
      label: 'Components',
      template: () => <MeaLink to={`/school/${currentMembership?.school?.id}/admin`} icon="pi pi-home" isRipple={true}>{currentMembership?.school?.name}</MeaLink>
    },
    { label: targetLabel }
  ];

  const ViewSwitcher = () => (
    <div className="flex align-items-center gap-2">      
      <div className="flex border-1 border-200 border-round">
        <Button
          icon="pi pi-th-large"
          className={`p-button-text ${viewMode === 'cards' ? 'bg-primary text-white' : 'text-600'}`}
          onClick={() => setViewMode('cards')}
          tooltip="Vue en cartes"
          tooltipOptions={{ position: 'bottom' }}
        />
        <Button
          icon="pi pi-list"
          className={`p-button-text ${viewMode === 'list' ? 'bg-primary text-white' : 'text-600'}`}
          onClick={() => setViewMode('list')}
          tooltip="Vue en liste"
          tooltipOptions={{ position: 'bottom' }}
        />
      </div>
    </div>
  );

  return (
    <>
      <MeaPage
        title={targetSection === "candidates" ? "De quelle formation ?" : `${targetLabel} - De quelle formation ?`}
        breadcrumb={<BreadCrumb model={breadcrum} />}
        customActions={<ViewSwitcher />}
      >
        <div className="p-3"> 
        {viewMode === 'cards' ? (
          <RegistrationsCardsComponent
            ref={listRef}
            showAddButton={true}
            targetSection={targetSection}
          />
        ) : (
          <RegistrationsListComponent
            ref={listRef}
            showAddButton={true}
            targetSection={targetSection}
          />
        )}
        </div>
      </MeaPage>

      <Dialog
        header="Sélectionner une formation"
        visible={!lastVisitedTrainingId && modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '500px' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        modal
        className="p-fluid"
      >
        <div className="flex flex-column gap-3 mt-2">
          <p className="text-600">Veuillez sélectionner une formation pour y accéder.</p>
          <div className="flex flex-column gap-2 overflow-y-auto" style={{ maxHeight: '400px' }}>
            {trainings.length === 0 ? (
              <div className="text-center py-4 text-500">Aucune formation disponible</div>
            ) : (
              trainings.map((t) => (
                <div 
                  key={t.id} 
                  className="p-3 border-1 border-200 border-round hover:bg-teal-50 cursor-pointer flex align-items-center justify-content-between transition-colors transition-duration-150"
                  onClick={() => handleSelectTraining(t.id)}
                >
                  <div className="flex align-items-center gap-3">
                    <i className="pi pi-book text-teal-500 text-xl"></i>
                    <span className="font-semibold text-900">{t.name}</span>
                    <span className={`text-xs px-2 py-0.5 border-round font-semibold ml-2 ${
                      t.status === 'OPEN' ? 'bg-green-100 text-green-800' :
                      t.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
                      t.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getStatusLabel(t.status)}
                    </span>
                  </div>
                  <i className="pi pi-chevron-right text-400"></i>
                </div>
              ))
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default RegistrationsListPage;
